import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { uploadVendorPrice } from "../../services/vendorService";
import { errorToast, successToast } from "../Toasts/Toasts";

const VendorUploadPrices = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { vendorId } = useParams();
  const { vendorName, vendorLocation } = location.state || {};

  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: { date: new Date() },
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0] || null);
    setUploadResult(null);
  };

  const handleUpload = async (data) => {
    if (!selectedFile) {
      errorToast("Please select an Excel file");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("vendorId", vendorId);
    formData.append("date", data.date.toISOString());

    try {
      setUploading(true);
      const response = await uploadVendorPrice(formData);

      if (response?.success) {
        const { insertedCount, alreadyExists, invalidSkus } = response.data;

        if (insertedCount > 0) {
          successToast(`${insertedCount} price(s) uploaded successfully`);
        }

        if (alreadyExists?.length > 0) {
          alreadyExists.forEach((sku) => {
            errorToast(`${sku} already exists for the selected date`);
          });
        }

        if (invalidSkus?.length > 0) {
          invalidSkus.forEach((sku) => {
            errorToast(`Invalid SKU: ${sku}`);
          });
        }

        setUploadResult(response.data);

        if (insertedCount > 0 && alreadyExists?.length === 0 && invalidSkus?.length === 0) {
          navigate("/vendor");
        }
      }
    } catch (error) {
      errorToast(error?.response?.data?.message || "Something went wrong");
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    reset({ date: new Date() });
    setSelectedFile(null);
    setUploadResult(null);
  };

  return (
    <div className="page-body px-0 py-lg-2 py-1 mt-0 mt-lg-3 ms-3">
      <div className="container-fluid p-0">
        <div className="row g-3">
          <div className="col-12">
            <div className="row-title px-2">
              <div>
                <h6 className="fw-bold">Vendor Management</h6>
              </div>
            </div>
          </div>

          <div className="container">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title mb-2 pb-2">Upload Prices (Excel)</h5>
                <div className="row">
                  <div className="col-12">
                    <div className="col-6">
                      <p className="font-weight-bold">Vendor Name: {vendorName}</p>
                    </div>
                    <div className="col-6">
                      <p className="font-weight-bold">Vendor Location: {vendorLocation}</p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit(handleUpload)}>
                  <div className="form-group">
                    <div className="form-group row">
                      <div className="col-lg-3 col-md-3 col-12 mb-3">
                        <label className="form-label">Date</label>
                        <Controller
                          name="date"
                          control={control}
                          rules={{ required: "Date is required" }}
                          render={({ field: { onChange, value, onBlur } }) => (
                            <DatePicker
                              dateFormat="dd/MM/YYYY"
                              selected={value}
                              onChange={onChange}
                              onBlur={onBlur}
                              className="form-control"
                              placeholderText="Select Date"
                            />
                          )}
                        />
                        {errors.date && <span className="text-danger">{errors.date.message}</span>}
                      </div>
                    </div>

                    <div className="form-group row">
                      <div className="col-lg-6 col-md-6 col-12 mb-3">
                        <label className="form-label">Excel File (.xlsx, .xls)</label>
                        <input
                          type="file"
                          className="form-control"
                          accept=".xlsx,.xls"
                          onChange={handleFileChange}
                        />
                        <small className="text-muted">
                          Excel must have "SKU" and "Price" columns
                        </small>
                      </div>
                    </div>
                  </div>

                  {uploadResult && (
                    <div className="row mb-3">
                      <div className="col-lg-6 col-md-6 col-12">
                        <div className="alert alert-info mb-0">
                          <strong>Upload Results:</strong>
                          <ul className="mb-0 mt-1">
                            <li>Inserted: {uploadResult.insertedCount}</li>
                            {uploadResult.alreadyExists?.length > 0 && (
                              <li>Already exists: {uploadResult.alreadyExists.join(", ")}</li>
                            )}
                            {uploadResult.invalidSkus?.length > 0 && (
                              <li>Invalid SKUs: {uploadResult.invalidSkus.join(", ")}</li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="row">
                    <div className="col-12">
                      <div className="d-flex justify-content-end">
                        <button
                          type="submit"
                          className="btn me-2"
                          style={{ color: "#ffffff", backgroundColor: "#00ad9a9c" }}
                          disabled={uploading}
                        >
                          {uploading ? "Uploading..." : "Upload"}
                        </button>
                        <Link onClick={handleReset} className="btn btn-secondary ms-2" to="/vendor">
                          Cancel
                        </Link>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorUploadPrices;
